
import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import { Funnel } from '@/types/funnels';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TopMetricsProps {
  funnels: Funnel[];
  onAddFunnel: (funnel: Omit<Funnel, 'id' | 'last_updated' | 'leads' | 'created_by'>) => void;
  onUpdateFunnel: (id: string, field: string, value: string | number) => void;
  onDeleteFunnel?: (id: string) => void;
}

const formatDuration = (startTime: string) => {
  if (!startTime) return 'N/A';
  
  try {
    const start = new Date(startTime).getTime();
    const now = new Date().getTime();
    const diff = now - start;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch (e) {
    return 'N/A';
  }
};

const TopMetrics: React.FC<TopMetricsProps> = ({ funnels, onAddFunnel, onUpdateFunnel, onDeleteFunnel }) => {
  const [open, setOpen] = React.useState(false);
  const [editingFunnel, setEditingFunnel] = React.useState<Funnel | null>(null);
  const [time, setTime] = useState(new Date());
  const [newFunnel, setNewFunnel] = React.useState({
    funnel_name: '',
    split_test: 'false',
    assets_link: '',
    funnel_link: '',
    runtime: '',
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleAddFunnel = () => {
    if (!newFunnel.funnel_name.trim()) return;
    onAddFunnel(newFunnel);
    setNewFunnel({
      funnel_name: '',
      split_test: 'false',
      assets_link: '',
      funnel_link: '',
      runtime: '',
    });
    setOpen(false);
  };

  const handleUpdateField = (field: string, value: string) => {
    if (!editingFunnel) return;
    onUpdateFunnel(editingFunnel.id, field, value);
  };

  const handleDeleteFunnel = (id: string) => {
    if (onDeleteFunnel) {
      onDeleteFunnel(id);
    }
  };

  return (
    <div className="space-y-6 mb-12">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Active Funnels</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gray-900 text-white hover:bg-gray-800 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Add Sales Funnel
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Sales Funnel</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Funnel Name</Label>
                <Input
                  value={newFunnel.funnel_name}
                  onChange={(e) => setNewFunnel(prev => ({ ...prev, funnel_name: e.target.value }))}
                  placeholder="Enter funnel name"
                  required
                />
              </div>
              <div className="space-y-2 flex items-center">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="splitTest"
                    checked={newFunnel.split_test === 'true'}
                    onCheckedChange={(checked) => 
                      setNewFunnel(prev => ({ ...prev, split_test: checked ? 'true' : 'false' }))
                    }
                  />
                  <Label htmlFor="splitTest">Split Test</Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assets Link</Label>
                <Input
                  type="url"
                  value={newFunnel.assets_link}
                  onChange={(e) => setNewFunnel(prev => ({ ...prev, assets_link: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Funnel Link</Label>
                <Input
                  type="url"
                  value={newFunnel.funnel_link}
                  onChange={(e) => setNewFunnel(prev => ({ ...prev, funnel_link: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Runtime</Label>
                <Input
                  type="datetime-local"
                  value={newFunnel.runtime}
                  onChange={(e) => setNewFunnel(prev => ({ ...prev, runtime: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Button 
                  className="w-full bg-gray-900 text-white hover:bg-gray-800"
                  onClick={handleAddFunnel}
                >
                  Add Funnel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {funnels.map(funnel => {
          const isRunning = funnel.runtime && funnel.runtime !== '';
          
          return (
            <Card key={funnel.id} className="p-6 hover:shadow-md transition-shadow duration-200 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <div className="relative">
                  <span className="absolute inset-0 animate-pulse rounded-full bg-purple-400/20 blur-sm"></span>
                  <span className="relative inline-flex items-center px-3 py-1 text-xs font-medium text-purple-900 bg-purple-100 rounded-full border border-purple-200">
                    Launching New Ads
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 mb-4">
                <div className="relative flex items-center">
                  <div className={`w-3 h-3 rounded-full ${isRunning ? 'bg-[#7ed957]' : 'bg-gray-300'}`}>
                    {isRunning && (
                      <div className="absolute w-3 h-3 rounded-full bg-[#7ed957] animate-ping" 
                           style={{ backgroundColor: '#7ed957', opacity: '0.5' }} />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-2">{funnel.funnel_name || 'Untitled Funnel'}</h3>
                </div>
                {funnel.split_test === 'true' && (
                  <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full whitespace-nowrap">
                    Split Test Active
                  </span>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col items-center text-center">
                    <Label className="text-xs text-gray-500">Runtime</Label>
                    <p className="font-medium whitespace-nowrap">
                      {isRunning ? formatDuration(funnel.runtime) : 'N/A'}
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <Label className="text-xs text-gray-500">Total Leads</Label>
                    <p className="font-medium">{typeof funnel.leads === 'number' ? funnel.leads : 0}</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-center gap-4">
                  <div className="flex flex-col sm:flex-row gap-3 items-center">
                    <div className="flex flex-col items-center text-center">
                      <Label className="text-xs text-gray-500 mb-2">Funnel Link</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(funnel.funnel_link || '#', '_blank')}
                        disabled={!funnel.funnel_link}
                        className="w-32"
                      >
                        View Funnel
                      </Button>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <Label className="text-xs text-gray-500 mb-2">Assets Link</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(funnel.assets_link || '#', '_blank')}
                        disabled={!funnel.assets_link}
                        className="w-32"
                      >
                        View Assets
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute top-4 right-4 flex items-center gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => setEditingFunnel(funnel)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Edit Funnel</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>Funnel Name</Label>
                        <Input
                          value={funnel.funnel_name}
                          onChange={(e) => handleUpdateField('funnel_name', e.target.value)}
                          placeholder="Enter funnel name"
                        />
                      </div>
                      <div className="space-y-2 flex items-center">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`splitTest-${funnel.id}`}
                            checked={funnel.split_test === 'true'}
                            onCheckedChange={(checked) => 
                              handleUpdateField('split_test', checked ? 'true' : 'false')
                            }
                          />
                          <Label htmlFor={`splitTest-${funnel.id}`}>Split Test</Label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Assets Link</Label>
                        <Input
                          type="url"
                          value={funnel.assets_link || ''}
                          onChange={(e) => handleUpdateField('assets_link', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Funnel Link</Label>
                        <Input
                          type="url"
                          value={funnel.funnel_link || ''}
                          onChange={(e) => handleUpdateField('funnel_link', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Runtime</Label>
                        <Input
                          type="datetime-local"
                          value={funnel.runtime || ''}
                          onChange={(e) => handleUpdateField('runtime', e.target.value)}
                        />
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Funnel</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{funnel.funnel_name}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => handleDeleteFunnel(funnel.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TopMetrics;
